import React, { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const initialState = {
  name: "",
  email: "",
  phone: "",
  company: "",
  country: "",
  product: "",
  quantity: "",
  message: "",
};

const inputClass =
  "w-full bg-transparent border-0 border-b border-line focus:border-forest focus:outline-none py-3 text-ink placeholder:text-muted/60 transition-colors duration-300";

const labelClass =
  "text-[10px] tracking-[0.25em] uppercase text-muted block mb-1";

const InquiryForm = ({ defaultProduct = "", compact = false }) => {
  const [form, setForm] = useState({ ...initialState, product: defaultProduct });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSuccess(false);

    const formData = new FormData(e.target);

    // Changed from "/" to "/api/submit-inquiry"
    fetch("/api/submit-inquiry", {
      method: "POST",
      body: formData, // Sending as FormData directly
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed");
        setSuccess(true);
        toast.success("Inquiry sent — we'll be in touch within one business day.");
        setForm({ ...initialState, product: defaultProduct });
      })
      .catch(() => {
        toast.error("Failed to submit inquiry.");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <form
      onSubmit={onSubmit}
      name="inquiry"
      method="POST"
      data-testid="inquiry-form"
      className="space-y-8"
    >

      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label className={labelClass} htmlFor="if-name">Full Name *</label>
          <input
            id="if-name"
            name="name"
            value={form.name}
            onChange={onChange}
            required
            data-testid="inquiry-name"
            className={inputClass}
            placeholder="John Doe"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="if-email">Email *</label>
          <input
            id="if-email"
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            required
            data-testid="inquiry-email"
            className={inputClass}
            placeholder="john@company.com"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="if-phone">Phone</label>
          <input
            id="if-phone"
            name="phone"
            value={form.phone}
            onChange={onChange}
            data-testid="inquiry-phone"
            className={inputClass}
            placeholder="+1 555 0100"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="if-company">Company</label>
          <input
            id="if-company"
            name="company"
            value={form.company}
            onChange={onChange}
            data-testid="inquiry-company"
            className={inputClass}
            placeholder="Acme Foods Ltd."
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="if-country">Country</label>
          <input
            id="if-country"
            name="country"
            value={form.country}
            onChange={onChange}
            data-testid="inquiry-country"
            className={inputClass}
            placeholder="United States"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="if-product">Product of Interest</label>
          <input
            id="if-product"
            name="product"
            value={form.product}
            onChange={onChange}
            data-testid="inquiry-product"
            className={inputClass}
            placeholder="e.g. Psyllium Husk 99%"
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="if-quantity">Estimated Quantity</label>
          <input
            id="if-quantity"
            name="quantity"
            value={form.quantity}
            onChange={onChange}
            data-testid="inquiry-quantity"
            className={inputClass}
            placeholder="e.g. 5 MT / month"
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="if-message">Message *</label>
          <textarea
            id="if-message"
            name="message"
            value={form.message}
            onChange={onChange}
            required
            rows={compact ? 4 : 5}
            data-testid="inquiry-message"
            className={inputClass + " resize-none"}
            placeholder="Tell us about your requirement, target specifications, certifications and shipping destination."
          />
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="text-xs text-muted">
          By submitting you agree to be contacted by Gralon regarding your inquiry.
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="inquiry-submit"
          className="inline-flex items-center gap-3 px-8 py-3.5 bg-forest text-cream text-sm tracking-[0.2em] uppercase hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              Sending
            </>
          ) : (
            <>
              <Send className="w-4 h-4" strokeWidth={1.5} />
              Send Inquiry
            </>
          )}
        </button>
      </div>

      {success && (
        <div
          data-testid="inquiry-success"
          className="border-l-2 border-gold bg-cream-alt p-5 text-sm text-ink"
        >
          <div className="font-serif text-xl text-forest-dark mb-1">Thank you.</div>
          Your inquiry has been received. Our export team will respond within one business day.
        </div>
      )}
    </form>
  );
};

export default InquiryForm;
